import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from "./config";

const localeSet = new Set<string>(SUPPORTED_LOCALES);

export const isSupportedLocale = (value?: string | null): value is Locale => {
  if (!value) return false;
  return localeSet.has(value.toLowerCase());
};

export const normalizeLocale = (value?: string | null): Locale => {
  if (!value) return DEFAULT_LOCALE;
  const trimmed = value.toLowerCase();
  return isSupportedLocale(trimmed) ? (trimmed as Locale) : DEFAULT_LOCALE;
};

export const getLocaleFromPathname = (pathname: string): Locale | null => {
  const segment = pathname.split("/").filter(Boolean)[0];
  if (!segment) return null;
  return isSupportedLocale(segment) ? (segment as Locale) : null;
};

export const stripLocaleFromPathname = (
  pathname: string,
  locale?: string | null
) => {
  const activeLocale = locale ?? getLocaleFromPathname(pathname);
  if (!activeLocale) return pathname || "/";

  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const parts = normalized.split("/").filter(Boolean);
  if (parts[0] !== activeLocale) {
    return normalized || "/";
  }
  const remainder = parts.slice(1).join("/");
  return remainder ? `/${remainder}` : "/";
};

export const withLocale = (path: string, locale: Locale) => {
  const basePath = stripLocaleFromPathname(path, getLocaleFromPathname(path));
  const normalized = basePath.startsWith("/") ? basePath : `/${basePath}`;
  return normalized === "/" ? `/${locale}` : `/${locale}${normalized}`;
};

export const buildPathWithSearch = (pathname: string, search: string) => {
  if (!search) return pathname;
  const normalized = search.startsWith("?") ? search : `?${search}`;
  return `${pathname}${normalized}`;
};
